import { combineReducers, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { deepRemoveUndefinedProperties } from '../../utils/deep-remove-undefined-properties';
import { findSection, insertSectionIntoTree } from '../../utils/page/section';
import { sameRegistration } from '../../utils/registration';
export const pageInitialState = {
    pages: {
        lastAddedNodeId: undefined,
        registeredPageFields: {},
        nodes: {}
    }
};
const pagesSlice = createSlice({
    name: 'pages',
    initialState: pageInitialState.pages,
    reducers: {
        page_updateOrCreate(state, action) {
            let { id, slug, jaenFields, sections, jaenPageMetadata, parentPage, childPages, template, excludedFromIndex, fromId, childPagesOrder = [], createdBy } = action.payload;
            const parentPageId = parentPage?.id || null;
            const modifiedAt = new Date().toISOString();
            if (id) {
                let toBeAddedData = {
                    id,
                    modifiedAt,
                    ...(slug && { slug }),
                    ...(jaenFields !== undefined && { jaenFields }),
                    ...(sections !== undefined && { sections }),
                    jaenPageMetadata,
                    ...(parentPage !== undefined && { parentPage }),
                    ...(childPages && { childPages }),
                    ...(excludedFromIndex !== undefined && { excludedFromIndex }),
                    childPagesOrder
                };
                toBeAddedData = deepRemoveUndefinedProperties(toBeAddedData);
                state.nodes[id] = {
                    ...state.nodes[id],
                    ...toBeAddedData,
                    jaenPageMetadata: {
                        ...state.nodes[id]?.jaenPageMetadata,
                        ...jaenPageMetadata
                    }
                };
                if (fromId && (parentPageId || parentPageId === null)) {
                    const newchildPages = [
                        ...(state.nodes[fromId]?.childPages || []).filter(e => e.id !== id),
                        {
                            id,
                            deleted: true
                        }
                    ];
                    state.nodes[fromId] = {
                        ...state.nodes[fromId],
                        modifiedAt,
                        childPages: newchildPages
                    };
                }
            }
            else {
                if (fromId) {
                    throw new Error(`Cannot move a page that is being created.`);
                }
                id = `JaenPage ${uuidv4()}`;
                state.nodes[id] = {
                    createdAt: modifiedAt,
                    modifiedAt,
                    createdBy,
                    slug,
                    jaenFields: jaenFields || null,
                    sections,
                    jaenPageMetadata: jaenPageMetadata || {
                        title: 'New Page'
                    },
                    parentPage: parentPage || null,
                    childPages: childPages || [],
                    template,
                    excludedFromIndex,
                    childPagesOrder
                };
                state.lastAddedNodeId = id;
            }
            if (parentPageId) {
                const childPages = state.nodes[parentPageId]?.childPages || [{ id }];
                if (!childPages.find(e => e.id === id)) {
                    childPages.push({ id });
                }
                state.nodes[parentPageId] = {
                    modifiedAt,
                    ...state.nodes[parentPageId],
                    childPages
                };
            }
            return state;
        },
        page_markForDeletion(state, action) {
            const id = action.payload;
            state.nodes[id] = {
                ...state.nodes[id],
                deleted: true,
                childPages: state.nodes[id]?.childPages || []
            };
            return state;
        },
        section_add(state, action) {
            const { pageId, path, sectionItemType, between } = action.payload;
            state.nodes[pageId] = {
                ...state.nodes[pageId],
                childPages: state.nodes[pageId]?.childPages || []
            };
            const page = state.nodes[pageId];
            const sections = page.sections || [];
            insertSectionIntoTree(sections, path, {
                between,
                blockData: {
                    type: sectionItemType
                }
            });
            page.sections = sections;
            return state;
        },
        section_remove(state, action) {
            const { pageId, path, sectionId, between } = action.payload;
            state.nodes[pageId] = {
                ...state.nodes[pageId],
                childPages: state.nodes[pageId]?.childPages || []
            };
            const page = state.nodes[pageId];
            const sections = page.sections || [];
            insertSectionIntoTree(sections, path, {
                between,
                sectionId,
                shouldDelete: true
            });
            page.sections = sections;
            return state;
        },
        section_move(state, action) {
            const { pageId, path, sectionId, between, move } = action.payload;
            state.nodes[pageId] = {
                ...state.nodes[pageId],
                childPages: state.nodes[pageId]?.childPages || []
            };
            const page = state.nodes[pageId];
            const sections = page.sections || [];
            insertSectionIntoTree(sections, path, {
                between,
                sectionId,
                move
            });
            page.sections = sections;
            return state;
        },
        section_register(state, action) {
            const { pageId, path, props } = action.payload;
            state.nodes[pageId] = {
                ...state.nodes[pageId],
                childPages: state.nodes[pageId]?.childPages || []
            };
            const page = state.nodes[pageId];
            const sections = page.sections || [];
            const section = findSection(sections, path);
            if (section) {
                section.props = props;
            }
            page.sections = sections;
            return state;
        },
        page_unregisterFields(state, action) {
            const { pageId } = action.payload;
            state.registeredPageFields = {
                ...state.registeredPageFields,
                [pageId]: 0
            };
        },
        field_register(state, action) {
            const { pageId, section, fieldType, fieldName, props } = action.payload;
            const registered = (() => {
                const existing = state.nodes[pageId];
                if (!existing) {
                    return undefined;
                }
                if (section) {
                    return findSection(existing.sections || [], section.path)?.items.find(item => item.id === section.id)?.jaenFields?.[fieldType]?.[fieldName];
                }
                return existing.jaenFields?.[fieldType]?.[fieldName];
            })();
            if (registered && sameRegistration(registered.props, props)) {
                return;
            }
            state.nodes[pageId] = {
                ...state.nodes[pageId],
                childPages: state.nodes[pageId]?.childPages || [],
                sections: state.nodes[pageId]?.sections || []
            };
            const page = state.nodes[pageId];
            if (section) {
                const sections = page.sections || [];
                insertSectionIntoTree(sections, section.path, {
                    sectionId: section.id,
                    blockData: {
                        jaenFields: {
                            [fieldType]: {
                                [fieldName]: {
                                    position: undefined,
                                    props
                                }
                            }
                        }
                    }
                });
            }
            else {
                page.jaenFields = page.jaenFields || {};
                page.jaenFields[fieldType] = {
                    ...page.jaenFields[fieldType],
                    [fieldName]: {
                        ...page.jaenFields[fieldType]?.[fieldName],
                        position: undefined,
                        props
                    }
                };
            }
        },
        field_write(state, action) {
            const { pageId, section, fieldType, fieldName, value, props } = action.payload;
            const modifiedAt = new Date().toISOString();
            state.nodes[pageId] = {
                ...state.nodes[pageId],
                modifiedAt,
                childPages: state.nodes[pageId]?.childPages || []
            };
            const page = state.nodes[pageId];
            if (section) {
                page.sections = page.sections || [];
                insertSectionIntoTree(page.sections, section.path, {
                    sectionId: section.id,
                    blockData: {
                        jaenFields: {
                            [fieldType]: {
                                [fieldName]: {
                                    value,
                                    props
                                }
                            }
                        }
                    }
                });
            }
            else {
                page.jaenFields = page.jaenFields || {};
                page.jaenFields[fieldType] = {
                    ...page.jaenFields[fieldType],
                    [fieldName]: {
                        ...page.jaenFields[fieldType]?.[fieldName],
                        value,
                        props
                    }
                };
            }
            return state;
        },
        hydrateFromRemote(state, action) {
            state.nodes = action.payload.nodes;
            return state;
        },
        discardAllChanges(state) {
            state.registeredPageFields = {};
            state.nodes = {};
            state.lastAddedNodeId = undefined;
            return state;
        },
        discardChanges(state, action) {
            const { pageId } = action.payload;
            delete state.nodes[pageId];
            if (state.lastAddedNodeId === pageId) {
                state.lastAddedNodeId = undefined;
            }
            return state;
        }
    }
});
export const actions = {
    ...pagesSlice.actions
};
export const reducers = {
    pages: pagesSlice.reducer
};
export default combineReducers(reducers);
